import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { BreadcrumbProps } from 'antd';
import type { TFunction } from 'i18next';

export type AdminBreadcrumbItem = NonNullable<BreadcrumbProps['items']>[number];

type RouteMeta = {
  path: string;
  labelKey: string;
  groupKey: string;
};

/** Keep in sync with AdminLayout menu IA (translation keys under nav.*). */
const ROUTE_META: RouteMeta[] = [
  { path: '/dashboard', labelKey: 'nav.dashboard', groupKey: 'nav.grpCommand' },
  { path: '/sos-events', labelKey: 'nav.sosEvents', groupKey: 'nav.grpCommand' },
  { path: '/schedule-overview', labelKey: 'nav.scheduleOverview', groupKey: 'nav.grpScheduling' },
  { path: '/jobs', labelKey: 'nav.jobs', groupKey: 'nav.grpScheduling' },
  { path: '/daily-schedule', labelKey: 'nav.dailySchedule', groupKey: 'nav.grpScheduling' },
  { path: '/trips', labelKey: 'nav.trips', groupKey: 'nav.grpScheduling' },
  { path: '/fleet-availability', labelKey: 'nav.fleetAvailability', groupKey: 'nav.grpScheduling' },
  { path: '/fleet-operators', labelKey: 'nav.fleetOperators', groupKey: 'nav.grpFleet' },
  { path: '/vehicles', labelKey: 'nav.vehicles', groupKey: 'nav.grpFleet' },
  { path: '/drivers', labelKey: 'nav.drivers', groupKey: 'nav.grpFleet' },
  { path: '/vehicle-inspections', labelKey: 'nav.vehicleInspections', groupKey: 'nav.grpFleet' },
  { path: '/organizations', labelKey: 'nav.organizations', groupKey: 'nav.grpFleet' },
  { path: '/reminder-records', labelKey: 'nav.reminderRecords', groupKey: 'nav.grpFleet' },
  { path: '/data-management', labelKey: 'nav.dataManagement', groupKey: 'nav.grpClients' },
  { path: '/settings', labelKey: 'nav.settings', groupKey: 'nav.grpSystem' },
  { path: '/system-config', labelKey: 'nav.systemConfig', groupKey: 'nav.grpSystem' },
  { path: '/reminder-settings', labelKey: 'nav.reminderSettings', groupKey: 'nav.grpSystem' },
  { path: '/roles', labelKey: 'nav.roles', groupKey: 'nav.grpSystem' },
  { path: '/users', labelKey: 'nav.users', groupKey: 'nav.grpSystem' },
  { path: '/owner-organizations', labelKey: 'nav.ownerOrganizations', groupKey: 'nav.grpFleet' },
  { path: '/team', labelKey: 'nav.teamMembers', groupKey: 'nav.grpFleet' },
  { path: '/fleet-profile', labelKey: 'nav.fleetProfile', groupKey: 'nav.grpFleet' },
];

function crumbTitle(label: string, to?: string): ReactNode {
  return to ? <Link to={to}>{label}</Link> : label;
}

/**
 * Build Ant Design breadcrumb items for the current admin pathname.
 * Example: Scheduling / Jobs
 */
export function resolveAdminBreadcrumbs(
  pathname: string,
  t: TFunction
): AdminBreadcrumbItem[] {
  const path = pathname.split('?')[0] || '/';

  if (path === '/' || path === '/login' || path === '/403') {
    return [];
  }

  if (/^\/trips\/[^/]+\/map$/.test(path)) {
    return [
      { title: t('nav.grpScheduling') },
      { title: crumbTitle(t('nav.trips'), '/trips') },
      { title: t('nav.gpsTrack') },
    ];
  }

  if (path === '/jobs/new') {
    return [
      { title: t('nav.grpScheduling') },
      { title: crumbTitle(t('nav.jobs'), '/jobs') },
      { title: t('jobs.breadcrumbNew') },
    ];
  }
  if (/^\/jobs\/[^/]+\/edit$/.test(path)) {
    const id = path.split('/')[2];
    return [
      { title: t('nav.grpScheduling') },
      { title: crumbTitle(t('nav.jobs'), '/jobs') },
      { title: crumbTitle(t('jobs.breadcrumbDetail'), `/jobs/${id}`) },
      { title: t('jobs.breadcrumbEdit') },
    ];
  }
  if (/^\/jobs\/[^/]+$/.test(path) && path !== '/jobs/new') {
    return [
      { title: t('nav.grpScheduling') },
      { title: crumbTitle(t('nav.jobs'), '/jobs') },
      { title: t('jobs.breadcrumbDetail') },
    ];
  }

  const exact = ROUTE_META.find((r) => r.path === path);
  if (exact) {
    return [{ title: t(exact.groupKey) }, { title: t(exact.labelKey) }];
  }

  const prefix = ROUTE_META.filter((r) => path.startsWith(`${r.path}/`)).sort(
    (a, b) => b.path.length - a.path.length
  )[0];
  if (prefix) {
    return [
      { title: t(prefix.groupKey) },
      { title: crumbTitle(t(prefix.labelKey), prefix.path) },
    ];
  }

  return [];
}
